import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CREATE_ORDER_SIDE, DEPTH } from 'src/common/constants';
import { currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { Button } from 'src/components/core/button';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { cancelActiveOrder } from 'src/setup/redux/slices/spotSlice';
import { ActiveOrder, Symbol, OrderTableContentProps } from 'src/types';

const ActiveOrdersTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterActiveOrders, setFilterActiveOrders] = useState([] as Array<ActiveOrder>);
    const currentSymbol: Symbol = useAppSelector((state) => state.spot.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const symbols = useAppSelector((state) => state.global.configV2.symbol);
    const activeOrders: Array<ActiveOrder> = useAppSelector((state) => state.spot.activeOpenOrders);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? activeOrders
            : activeOrders.filter((activeOrder) => activeOrder.symbolId === symbolId);
        filtered = [...filtered].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
        setFilterActiveOrders(filtered);
    }, [activeOrders, symbolId, isShowAll]);

    const cancelOrder = (orderId: string) => {
        dispatch(
            cancelActiveOrder({
                order_id: orderId,
                client_order_id: new Date().getTime(),
            }),
        );
    };

    const columns = [
        {
            title: t('spot_pairs'),
            key: 'spotPairs',
            width: '5%',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {order.baseTokenId}
                        <span className="greyText">/{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            width: '5%',
            render: (order: ActiveOrder) => {
                return <span>{t(order.type.toLowerCase())}</span>;
            },
        },
        {
            title: t('direction'),
            key: 'direction',
            width: '4%',
            render: (order: ActiveOrder) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText: order.side === CREATE_ORDER_SIDE.BUY,
                            },
                            {
                                redText: order.side === CREATE_ORDER_SIDE.SELL,
                            },
                        )}
                    >
                        {t(order.side.toLowerCase())}
                    </span>
                );
            },
        },
        {
            title: t('order_value'),
            key: 'orderValue',
            width: '6%',
            render: (order: ActiveOrder) => {
                const orderValue = order.price * order.origQty;
                return (
                    <span>
                        {currencyFormatter(
                            orderValue,
                            DEPTH[symbols[order.symbolId]?.quotePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            width: '6%',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.price,
                            DEPTH[symbols[order.symbolId]?.minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_qty'),
            key: 'orderQty',
            width: '6%',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.origQty,
                            DEPTH[symbols[order.symbolId]?.basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_qty'),
            key: 'filledQty',
            width: '6%',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.executedQty,
                            DEPTH[symbols[order.symbolId]?.basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('unfilled_qty'),
            key: 'unfilledQty',
            width: '6%',
            render: (order: ActiveOrder) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.origQty - order.executedQty,
                            DEPTH[symbols[order.symbolId]?.basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            width: '8%',
            render: (order: ActiveOrder) => {
                return <span>{DateTimeFormatter(Number(order.time))}</span>;
            },
        },
        {
            title: t('order_id'),
            key: 'orderId',
            width: '9%',
            render: (order: ActiveOrder) => {
                return <span>{order.orderId}</span>;
            },
        },
        {
            title: t('action'),
            key: 'action',
            width: '4%',
            render: (order: ActiveOrder) => {
                return (
                    <FlexContainer styles={{ justifyContent: 'start' }}>
                        <Button
                            variant="outlined"
                            className="action-button"
                            onClick={() => cancelOrder(order.orderId)}
                        >
                            {t('cancel')}
                        </Button>
                    </FlexContainer>
                );
            },
        },
    ];

    return <StyledTable columns={columns} data={filterActiveOrders} rowKey="orderId" />;
};

export { ActiveOrdersTable };
